/**
 * Where the dialogue window stands (ADR-136 decision 5.6; the author's look of 2026-10-28: "the speech box covers the
 * chat etc. It needs to be placed in a better spot, more compact"): centred over the action bar, just above it, unless
 * that covers the chat panel, in which case it stands just above the chat panel's top at its left edge. Everything is in
 * CSS pixels of the overlay; the answer is whole pixels and kept inside the area. Pure.
 */

/**
 * A box in CSS pixels.
 */
export interface PlaceBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Whether two boxes overlap, each grown by a gap.
 *
 * @param a One box.
 * @param b The other.
 * @param gap The clearance kept between them.
 * @returns True when they come within the gap of each other.
 */
function near(a: PlaceBox, b: PlaceBox, gap: number): boolean {
  return a.left < b.left + b.width + gap && b.left < a.left + a.width + gap && a.top < b.top + b.height + gap && b.top < a.top + a.height + gap;
}

/**
 * The window's top-left corner.
 *
 * @param area The overlay's width and height.
 * @param size The window's width and height.
 * @param bar The action bar's box, or null when the page shows none (the window then stands at the area's foot).
 * @param chat The chat panel's box, or null when it shows none.
 * @param gap The clearance kept from the bar, the chat and the area's edges.
 * @returns The corner.
 * @example
 * dialoguePlace({ width: 1920, height: 1080 }, { width: 780, height: 150 }, bar, chat, 6);
 */
export function dialoguePlace(area: { readonly width: number; readonly height: number }, size: { readonly width: number; readonly height: number }, bar: PlaceBox | null, chat: PlaceBox | null, gap: number): { readonly left: number; readonly top: number } {
  const centre = bar === null ? area.width / 2 : bar.left + bar.width / 2;
  const foot = bar === null ? area.height - gap : bar.top - gap;
  const over: PlaceBox = { left: clamp(Math.round(centre - size.width / 2), gap, area.width - size.width - gap), top: clamp(Math.round(foot - size.height), gap, area.height - size.height - gap), width: size.width, height: size.height };
  if (chat === null || !near(over, chat, gap)) {
    return { left: over.left, top: over.top };
  }
  const beside = Math.round(chat.left + chat.width + gap);
  if (beside + size.width <= area.width - gap) {
    return { left: beside, top: over.top };
  }
  return { left: clamp(Math.round(chat.left), gap, area.width - size.width - gap), top: clamp(Math.round(chat.top - gap - size.height), gap, area.height - size.height - gap) };
}

/**
 * A value held between two bounds, the lower winning when they cross.
 *
 * @param value The value.
 * @param low The lower bound.
 * @param high The upper bound.
 * @returns The held value.
 */
function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(value, high));
}
